import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { AnyFieldApi } from '@tanstack/react-form';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { useToast } from '../../../../components/ui/Toast';
import { z } from 'zod';
import { productHooks } from '../../../../features/products/hooks';
import { Input, FormField } from '../../../../components/ui/FormField';
import { Banner } from '../../../../components/ui/Banner';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { getSession } from '../../../../lib/session';

export const Route = createFileRoute('/_auth/admin/products/new')({
  beforeLoad: ({ location }) => {
    const session = getSession();
    const isAllowed =
      session?.user.type === 'group_admin' ||
      session?.user.type === 'company_admin' ||
      session?.permissions.includes('product.create');
    if (!isAllowed) {
      throw redirect({ to: '/', search: { callbackUrl: location.href } });
    }
  },
  component: ProductCreate,
});

export const productValidator = z.object({
  productCode: z.string().min(1, 'Product code is required'),
  displayName: z.string().min(1, 'Display name is required'),
  cycleEngine: z.string().min(1, 'Cycle engine is required'),
  parameterSchemaString: z.string().refine((val) => {
    try {
      JSON.parse(val);
      return true;
    // allow-silent-catch: JSON.parse failure is the expected validation-failure path here, returning false is the intended handling
    } catch {
      return false;
    }
  }, 'Must be a valid JSON string'),
  isActive: z.boolean(),
});

type FormValues = z.infer<typeof productValidator>;

const productFormConfig = defineFormPage<FormValues>({
  resource: 'products',
  defaultValues: {
    productCode: '',
    displayName: '',
    cycleEngine: 'CHIT_CYCLE',
    parameterSchemaString: '{\n  "minDurationMonths": 10,\n  "maxDurationMonths": 50,\n  "minMemberCount": 10,\n  "maxMemberCount": 50\n}',
    isActive: true,
  },
  schema: productValidator,
});

function ProductCreate() {
  const navigate = useNavigate();
  const createMutation = productHooks.useCreate();
  const { toast } = useToast();

  const {
    form,
    isSubmitting,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(productFormConfig, createMutation, {
    routeId: 'admin/products/new',
    onBeforeSubmit: (value) => {
      const { parameterSchemaString, ...rest } = value;
      return {
        ...rest,
        parameterSchema: JSON.parse(parameterSchemaString),
      };
    },
    onSuccess: () => {
      toast({ type: 'success', title: 'Success', description: 'Product created successfully.' });
      navigate({ to: '/admin/products' });
    },
    onError: (err: { message?: string }) => {
      toast({ type: 'error', title: 'Error', description: err?.message || 'Failed to create product.' });
    },
  });

  return (
    <div className="max-w-[720px]">
      <PageHeader
        title="Create Product"
        subtitle="Define a new financial product with its parameter validation schema and cycle engine."
        backTo="/admin/products"
      />

      <form
        onSubmit={handleSubmit}
        className="bg-surface-1 border border-border shadow-sm rounded-[14px] p-6 lg:p-8 space-y-6"
      >
        {hasDraft && (
          <div className="mb-6">
            <Banner
              variant="info"
              title="Restore Draft"
              description="You have an unsaved draft from a previous session. Would you like to restore it?"
              action={{
                label: 'Restore',
                onClick: () => {
                  const draft = restoreDraft();
                  if (draft) {
                    Object.entries(draft).forEach(([key, val]) => {
                      form.setFieldValue(key as never, val as never);
                    });
                  }
                  dismissDraft();
                },
              }}
              secondaryAction={{
                label: 'Discard',
                onClick: () => {
                  clearDraft();
                },
              }}
            />
          </div>
        )}

        <form.Field
          name="productCode"
          validators={{
            onChange: ({ value }: { value: unknown }) =>
              productValidator.shape.productCode.safeParse(value).success
                ? undefined
                : productValidator.shape.productCode.safeParse(value).error?.issues[0]?.message,
          }}
          children={(field: AnyFieldApi) => (
            <FormField label="Product Code" required error={fe(field.state.meta)}>
              <Input
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. CHIT_FUND_V1"
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="displayName"
          validators={{
            onChange: ({ value }: { value: unknown }) =>
              productValidator.shape.displayName.safeParse(value).success
                ? undefined
                : productValidator.shape.displayName.safeParse(value).error?.issues[0]?.message,
          }}
          children={(field: AnyFieldApi) => (
            <FormField label="Display Name" required error={fe(field.state.meta)}>
              <Input
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. Chit Fund Standard Plan"
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="cycleEngine"
          validators={{
            onChange: ({ value }: { value: unknown }) =>
              productValidator.shape.cycleEngine.safeParse(value).success
                ? undefined
                : productValidator.shape.cycleEngine.safeParse(value).error?.issues[0]?.message,
          }}
          children={(field: AnyFieldApi) => (
            <FormField label="Cycle Engine" required error={fe(field.state.meta)}>
              <Input
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. CHIT_CYCLE"
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="parameterSchemaString"
          validators={{
            onChange: ({ value }: { value: unknown }) =>
              productValidator.shape.parameterSchemaString.safeParse(value).success
                ? undefined
                : productValidator.shape.parameterSchemaString.safeParse(value).error?.issues[0]?.message,
          }}
          children={(field: AnyFieldApi) => (
            <FormField
              label="Parameter Schema (JSON)"
              required
              error={fe(field.state.meta)}
              hint="Must be a valid JSON object defining limits and rules."
            >
              <textarea
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                rows={6}
                className="w-full bg-surface-2 border border-border text-text-primary rounded-[8px] p-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-accent-500"
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="isActive"
          children={(field: AnyFieldApi) => (
            <div className="flex items-center gap-2">
              <Input
                type="checkbox"
                id="isActive"
                checked={field.state.value}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-accent-500 w-4 h-4 rounded"
                disabled={isSubmitting}
              />
              <label htmlFor="isActive" className="text-sm font-medium text-text-primary select-none">
                Active & Available for new Schemes
              </label>
            </div>
          )}
        />

        <div className="flex justify-end items-center gap-3 pt-6 border-t border-border">
          <FormActions
            onCancel={() => navigate({ to: '/admin/products' })}
            isSubmitting={isSubmitting}
            submitLabel="Create Product"
          />
        </div>
      </form>
    </div>
  );
}
